import React from 'react';
import classnames from 'classnames';
import type { HTMLAttributes, InputHTMLAttributes } from 'react';
import type { FieldGroupProps } from './types';
import type { ChoiceType } from '../choice/types';
import type { AllColorType, AllIconType } from '../../util/types';
import Label from './Label';
import Choice from '../choice/Choice';
import Toggle from '../toggle/Toggle';
import { getIconComponent } from '../../util/reactFunc';

type Ref = HTMLInputElement;
type HTMLProps = Omit<InputHTMLAttributes<Ref>, 'type' | 'id'>;
interface Props extends HTMLProps, FieldGroupProps {
  /**
   * Choice input type (radio, checkbox) + custom toggle type.
   */
  type?: ChoiceType | 'toggle';
  /**
   * Sets a toggle with default states on/off.
   */
  isOnOff?: boolean;
  /**
   * Styles checkboxes types indeterminate.
   * https://www.w3schools.com/cssref/sel_indeterminate.asp
   */
  indeterminate?: boolean;
  /**
   * Sets label position to the left/right of the input.
   */
  labelPosition?: 'left' | 'right';
  /**
   * Passes additional props to label tag.
   */
  propsForLabelWrapper?: HTMLAttributes<HTMLSpanElement> | null;
  /**
   * Adds an additional icon to the input label.
   */
  icon?: AllIconType;
  iconColor?: AllColorType;
}

const ChoiceGroup = React.forwardRef<Ref, Props>(({
  id,
  type = 'checkbox',
  isOnOff,
  indeterminate = false,
  required = false,
  label,
  hideLabel = false,
  labelPosition = 'right',
  propsForLabelWrapper = null,
  icon,
  iconColor = 'dark',
  ...other
}: Props, ref): JSX.Element => (
  <div className={`flex ${classnames({ 'flex-row-reverse': labelPosition === 'left' })} items-center`}>
    {type === 'toggle' ? (
      <Toggle
        ref={ref}
        id={id}
        label={label}
        required={required}
        isOnOff={isOnOff}
        {...other as Omit<HTMLProps, 'color'>}
      />
    ) : (
      <Choice
        ref={ref}
        id={id}
        type={type}
        label={label}
        indeterminate={indeterminate}
        required={required}
        {...other}
      />
    )}
    {icon &&
      <span className={'ml-sm'}>
        {getIconComponent(icon, 'sm', iconColor)}
      </span>
    }
    <Label htmlFor={id} required={required} hideLabel={hideLabel} style={{
      marginLeft: labelPosition === 'left' ? 0 : 8,
      marginRight: labelPosition === 'right' ? 0 : 8,
      cursor: 'pointer',
    }}>
      {null !== propsForLabelWrapper ? (
        <span
          dangerouslySetInnerHTML={{ __html: label }}
          {...propsForLabelWrapper}
        />
      ) : label}
    </Label>
  </div>
));

ChoiceGroup.displayName = 'ChoiceGroup';

export default ChoiceGroup;
